import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import { QuoteLineType } from '../../types/state/quote-line';
import { QuotePriceType } from '../../types/state/quote-price';

/**
 * Convenience function to throw an error if the quote line is missing
 *
 * Use this in actions that modify quote builder state.
 *
 * @param {WritableDraft<QuoteBuilderStateType>} state - current state of the quote builder
 * @param {string} quoteLineUuid - uuid of the quote line to retrieve
 * @returns {QuoteLineType} the quote line with the provided uuid
 * @throws {Error} if the quote line is not found
 */
export function getQuoteLineOrThrow(
  state: WritableDraft<QuoteBuilderStateType>,
  quoteLineUuid: string,
): QuoteLineType {
  if (!state.quoteLines[quoteLineUuid]) {
    throw new Error(`Quote line with uuid ${quoteLineUuid} not found`);
  }

  return state.quoteLines[quoteLineUuid];
}

/**
 * Convenience function to throw an error if the quote price is missing
 *
 * Use this in actions that modify quote builder state.
 *
 * @param {WritableDraft<QuoteBuilderStateType>} state - current state of the quote builder
 * @param {string} quoteLineUuid - uuid of the quote line that the quote price belongs to
 * @param {string} quotePriceUuid - uuid of the quote price to retrieve
 * @returns {QuotePriceType} the quote price with the provided uuid
 * @throws {Error} if the quote price is not found
 */
export function getQuotePriceOrThrow(
  state: WritableDraft<QuoteBuilderStateType>,
  quoteLineUuid: string,
  quotePriceUuid: string,
): QuotePriceType {
  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  if (!quoteLine.quotePrices[quotePriceUuid]) {
    throw new Error(`Quote price with uuid ${quotePriceUuid} not found`);
  }

  return quoteLine.quotePrices[quotePriceUuid];
}
